import React, { useState, useEffect, useContext } from 'react';
import { Message } from '../../contexts/ConversationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { LensContext } from '../../contexts/LensContext';
import { MessageActions } from './MessageActions';
import { BubbleForkAffordance } from './BubbleForkAffordance';
import { PersistedPhaseTrail } from './PhaseTrailBlock';
import type { PhaseEvent } from '../../hooks/usePersonaToolProgress';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { remarkSpineCitation } from '../../utils/remarkSpineCitation';
import { CitationPill } from './CitationPill';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github-dark.css';
import mermaid from 'mermaid';
import hljs from 'highlight.js';
import MermaidBlock, { cleanupMermaidErrors } from './MermaidBlock';
import { ToolCallPill, ToolCallEntry } from './ToolCallPill';
import { TurnTelemetryFooter, TurnTelemetryShape } from './TurnTelemetryFooter';
import { formatMessageTime } from './formatMessageTime';
import { MapInlineRenderer } from '../chat/panel/renderers/MapInlineRenderer';
import { MermaidInlineRenderer } from '../chat/panel/renderers/MermaidInlineRenderer';
import { dedupGisToolCalls, dedupMermaidToolCalls } from '../../utils/inlineMapToolFilter';
import { createArtifact } from '../../services/artifactService';
import { useNavigate } from 'react-router-dom';

// Task 28 — Group tool calls into their LLM rounds so the bubble can
// render a `— Round N — X calls —` divider between rounds. Calls with
// no llm_round stamp (older persisted messages) fall into round 1 so
// they still render as a single group.
interface RoundGroup {
  round: number;
  calls: ToolCallEntry[];
}
const groupByRound = (calls: any[]): RoundGroup[] => {
  const grouped = new Map<number, ToolCallEntry[]>();
  for (const call of calls) {
    const round = (call?.timing?.llm_round ?? call?.llm_round ?? 1) as number;
    if (!grouped.has(round)) grouped.set(round, []);
    grouped.get(round)!.push(call as ToolCallEntry);
  }
  return Array.from(grouped.entries())
    .map(([round, calls]) => ({ round, calls }))
    .sort((a, b) => a.round - b.round);
};

// Helper function to extract text from React children
const extractTextFromChildren = (children: any): string => {
  if (typeof children === 'string') return children;
  if (typeof children === 'number') return String(children);
  if (Array.isArray(children)) return children.map(extractTextFromChildren).join('');
  if (React.isValidElement(children)) {
    return extractTextFromChildren((children.props as any).children);
  }
  return '';
};

// Override console.error to filter out mermaid syntax errors
const originalConsoleError = console.error;
console.error = function(...args) {
  // Check if this is a mermaid syntax error
  const errorString = args.join(' ');
  if (errorString.includes('mermaid') && 
      (errorString.includes('Syntax error') || 
       errorString.includes('Parse error') || 
       errorString.includes('Lexical error'))) {
    // Log a minimal version of the error
    originalConsoleError.apply(console, ['Mermaid rendering error (suppressed full trace)']);
    return;
  }
  
  // Pass through all other errors
  originalConsoleError.apply(console, args);
};

// Add a global error handler for uncaught exceptions related to mermaid
window.addEventListener('error', function(event) {
  if (event.error && 
      event.error.toString().includes('mermaid')) {
    // Prevent the error from propagating
    event.preventDefault();
    event.stopPropagation();
    
    // Clean up any error elements that mermaid might have added
    cleanupMermaidErrors();
    
    return true;
  }
}, true);

// cleanupMermaidErrors is now imported from MermaidBlock.tsx

// Configure mermaid globally to completely disable error rendering
try {
  mermaid.initialize({
    startOnLoad: false,
    theme: 'default',
    securityLevel: 'strict',
    logLevel: 'fatal',
    fontFamily: 'sans-serif',
    er: { useMaxWidth: true },
    flowchart: { useMaxWidth: true },
    sequence: { useMaxWidth: true },
    // Completely disable error handling to prevent DOM pollution
    errorHandler: (error) => {
      console.warn('Mermaid error suppressed:', error.message || 'Unknown error');
      // Clean up any elements that might have been created
      cleanupMermaidErrors();
      return true;
    }
  });
  
  // Override mermaid's error handling
  if (typeof mermaid.mermaidAPI !== 'undefined') {
    if (mermaid.mermaidAPI.setErrorHandler) {
      mermaid.mermaidAPI.setErrorHandler((error) => {
        console.warn('Mermaid API error suppressed:', error);
        cleanupMermaidErrors();
        return true;
      });
    }
    
    // Don't try to override the render method as it's read-only
    // Instead, we'll handle errors in our own render function
  }
} catch (error) {
  console.warn("Failed to initialize mermaid:", error);
}

// Register a no-op language for mermaid to prevent errors
try {
  if (!hljs.getLanguage('mermaid')) {
    hljs.registerLanguage('mermaid', () => ({
      name: 'mermaid',
      contains: [],
      aliases: ['mermaid-diagram', 'mermaid-syntax'],
      keywords: {
        keyword: 'graph flowchart sequenceDiagram classDiagram stateDiagram erDiagram gantt pie journey',
      }
    }));
    console.log("Successfully registered mermaid language for highlighting");
  }
} catch (error) {
  console.warn("Failed to register mermaid language for highlighting:", error);
  // Continue anyway - we'll handle it gracefully
}

interface MessageBubbleProps {
  message: Message;
  showTimestamp?: boolean;
  modelName?: string;
  sessionId?: string;
  personaId?: string;
  personaName?: string;
}

export const MessageBubble: React.FC<MessageBubbleProps> = React.memo(({
  message,
  showTimestamp = true,
  modelName,
  sessionId,
  personaId,
  personaName
}) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  // Reply-targeting §3 — nullable: bubbles render on non-council surfaces
  // (admin/work chat) outside the LensProvider; those get no ↩ affordance.
  const lensCtx = useContext(LensContext);
  const isReplyTarget = lensCtx?.cursorTurnId === message.id;
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  // Task 28 — pill grid is tucked away by default. The telemetry
  // footer's `N tools` chip toggles visibility. One disclosure per
  // assistant message, state is local (no need to persist across
  // refetches — it's a quick drill-down, not a durable preference).
  const [toolCallsOpen, setToolCallsOpen] = useState(false);
  // Compact chip-fire bubble (one-tap chips fired from WorkshopRow / ChipStrip) collapses
  // by default — the chip label is the read; the full prompt body is
  // a drill-down behind a chevron. Mirrors the tool-call pill / phase
  // trail pattern (progressive disclosure for templated content).
  const [chipFireExpanded, setChipFireExpanded] = useState(false);

  // Re-initialize mermaid when theme changes
  useEffect(() => {
    try {
      mermaid.initialize({
        startOnLoad: false,
        theme: darkMode ? 'dark' : 'default',
        securityLevel: 'strict',
        logLevel: 'fatal',
      });
      cleanupMermaidErrors();
    } catch (error) {
      console.warn("Failed to initialize mermaid:", error);
    }
  }, [darkMode]);
  
  // Add a mutation observer to catch and remove error elements as soon as they're added
  useEffect(() => {
    // Create a mutation observer to watch for mermaid error elements
    const observer = new MutationObserver((mutations) => {
      let needsCleanup = false;
      
      mutations.forEach(mutation => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;
              
              // Check if this is a mermaid error element
              if (element.id?.includes('mermaid-') || 
                  element.id?.startsWith('dmermaid-') ||
                  element.getAttribute('aria-roledescription') === 'error') {
                needsCleanup = true;
              }
            }
          });
        }
      });
      
      if (needsCleanup) {
        cleanupMermaidErrors();
      }
    });
    
    // Start observing the document body for added nodes
    observer.observe(document.body, { 
      childList: true, 
      subtree: true 
    });
    
    // Clean up the observer when the component unmounts
    return () => {
      observer.disconnect();
      cleanupMermaidErrors();
    };
  }, []);

  // sanitizeMermaidCode is now in MermaidBlock.tsx

  // Function to copy code to clipboard
  const copyToClipboard = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  // Simple component to render code blocks with copy button
  const CodeBlock = ({ className, children, ...props }: any) => {
    const match = /language-(\w+)/.exec(className || '');
    const codeText = extractTextFromChildren(children).replace(/\n$/, '');
    
    // Mermaid code blocks delegate to the standalone MermaidBlock component
    // (extracted to fix Rules of Hooks — hooks were previously called conditionally here)
    if (match && match[1] === 'mermaid') {
      return <MermaidBlock code={codeText} darkMode={darkMode}>{children}</MermaidBlock>;
    }

    return (
      <div className="relative group">
        <button
          onClick={() => copyToClipboard(codeText)}
          className="absolute right-2 top-2 p-1 rounded bg-gray-700 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity"
          aria-label="Copy code"
        >
          {copiedCode === codeText ? (
            <span className="text-green-500">✓</span>
          ) : (
            <span>📋</span>
          )}
        </button>
        <pre className={className}>
          <code className={match ? match[1] : ''}>{children}</code>
        </pre>
      </div>
    );
  };


  // Persisted phase trail — rendered above the assistant message bubble
  // whenever `metadata.phase_events` exists and is non-empty. Works for
  // both just-landed and reload-rehydrated messages, for ANY tool (not
  // just consult_peers). Collapsed by default so historical scrollback
  // doesn't surface every turn's full trail.
  const persistedPhaseEvents: PhaseEvent[] | undefined =
    message.role === 'assistant'
      ? ((message.metadata as any)?.phase_events as PhaseEvent[] | undefined)
      : undefined;

  // Templated user message (e.g. fired from a WorkshopRow / ChipStrip quick-action
  // chip) — `metadata.template.label` carries the chip's display label
  // so we can render a compact bubble (label only, prompt body collapsed)
  // instead of dumping the full multi-paragraph prompt as a wall of
  // blue. Generic shape so non-chip callers (future Compose Menu Quick
  // Prompts, slash commands, etc.) can opt into the same rendering by
  // passing `template` metadata at send time.
  const chipTemplate: { label?: string; id?: string; source?: string } | null =
    message.role === 'user'
      ? (message.metadata as any)?.template ?? null
      : null;
  const isChipFire = !!chipTemplate?.label;

  return (
    <>
      {persistedPhaseEvents && persistedPhaseEvents.length > 0 && (
        <PersistedPhaseTrail events={persistedPhaseEvents} darkMode={darkMode} />
      )}
    <div
      data-message-id={message.id}
      className={`mb-4 ${message.role === 'user' ? 'flex justify-end' : 'flex justify-start'}`}
    >
      {isChipFire ? (
        <div
          className={`relative group rounded-lg max-w-[420px] ${
            darkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'
          } ring-1 ring-blue-300/30 shadow-sm`}
        >
          {sessionId && personaId && personaName && (
            <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
              <MessageActions
                messageContent={message.content}
                sessionId={sessionId}
                personaId={personaId}
                personaName={personaName}
                messageId={message.id}
              />
            </div>
          )}
          <button
            type="button"
            onClick={() => setChipFireExpanded((v) => !v)}
            aria-expanded={chipFireExpanded}
            aria-label={`${chipFireExpanded ? 'Collapse' : 'Expand'} chip prompt: ${chipTemplate?.label ?? ''}`}
            className="flex items-center gap-1.5 w-full text-left px-3 py-1.5 text-sm font-medium rounded-lg"
          >
            <span className="opacity-70 text-xs">{chipFireExpanded ? '▾' : '▸'}</span>
            <span className="truncate">{chipTemplate?.label}</span>
            <span
              className="ml-auto shrink-0 text-[9px] font-mono uppercase tracking-widest opacity-60"
              title="Sent from a quick-action chip"
            >
              chip
            </span>
          </button>
          {chipFireExpanded && (
            <div
              className={`mx-2 mb-2 px-2 py-1.5 rounded text-[11px] font-mono leading-snug whitespace-pre-wrap ${
                darkMode
                  ? 'bg-blue-700/50 text-blue-50'
                  : 'bg-blue-600/40 text-blue-50'
              }`}
            >
              {message.content}
            </div>
          )}
          {showTimestamp && (
            <div className="px-3 pb-1 text-[11px] text-blue-200">
              {formatMessageTime(message.timestamp)}
            </div>
          )}
        </div>
      ) : (
      <div
        className={`relative group rounded-lg px-4 py-2 ${
          message.role === 'user'
            ? `max-w-[75%] ${darkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'}`
            : message.role === 'error'
              ? `max-w-[75%] ${darkMode ? 'bg-red-900 text-white' : 'bg-red-100 text-red-800'}`
              : `max-w-[95%] ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900 border border-gray-200'}`
        } ${isReplyTarget ? (darkMode ? 'ring-2 ring-violet-400' : 'ring-2 ring-violet-500') : ''}`}
      >
        {/* Send-to + reply-target actions on hover. The ↩ deliberately
            INHERITS this cluster's guard (council surfaces always pass these
            props; user-role bubbles included — main-spine selection works). */}
        {sessionId && personaId && personaName && (message.role === 'user' || message.role === 'assistant') && (
          <div className="absolute top-1 right-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
            {lensCtx && (
              <button
                type="button"
                data-reply-target-button
                aria-label="Reply to this message"
                title={isReplyTarget ? 'Clear reply target' : 'Reply to this message'}
                onClick={(e) => {
                  e.stopPropagation();
                  lensCtx.setLens({ cursorTurnId: isReplyTarget ? null : message.id });
                }}
                className={`p-1 rounded text-xs leading-none ${
                  isReplyTarget
                    ? (darkMode ? 'bg-violet-800 text-violet-100' : 'bg-violet-200 text-violet-900')
                    : (darkMode ? 'bg-gray-700 text-gray-200 hover:bg-violet-800' : 'bg-gray-100 text-gray-700 hover:bg-violet-200')
                }`}
              >
                ↩
              </button>
            )}
            {lensCtx && (message as any).author_kind === 'peer' && (
              <BubbleForkAffordance
                darkMode={darkMode}
                onNewPath={() => lensCtx.setLens({ cursorTurnId: message.id, cursorMode: 'fork' })}
                onReroll={() => lensCtx.setLens({ cursorTurnId: message.id, cursorMode: 'fork', cursorReroll: true })}
              />
            )}
            <MessageActions
              messageContent={message.content}
              sessionId={sessionId}
              personaId={personaId}
              personaName={personaName}
              messageId={message.id}
            />
          </div>
        )}

        <div className="prose prose-sm dark:prose-invert max-w-none">
          {/* Use a try-catch to handle potential markdown parsing errors */}
          {(() => {
            // Pre-process content to fix malformed Mermaid blocks from LLMs
            let processedContent = message.content;
            try {
              // Fix: LLM outputs "📋\ngantt\n..." or "📋\npie title..." without proper fences
              // Detect known Mermaid diagram types appearing outside code fences and wrap them
              const mermaidTypes = ['pie ', 'pie\n', 'gantt', 'flowchart ', 'flowchart\n', 'sequenceDiagram', 'classDiagram', 'graph ', 'graph\n', 'timeline', 'erDiagram', 'xychart'];
              const lines = processedContent.split('\n');
              const fixedLines: string[] = [];
              let inMermaid = false;

              for (let i = 0; i < lines.length; i++) {
                const trimmed = lines[i].trim();
                const isAlreadyFenced = i > 0 && fixedLines[fixedLines.length - 1]?.trim().startsWith('```');

                if (!inMermaid && !isAlreadyFenced && mermaidTypes.some(t => trimmed.startsWith(t))) {
                  // Check it's not already inside a code fence
                  const prevNonEmpty = fixedLines.filter(l => l.trim()).slice(-1)[0]?.trim() || '';
                  if (!prevNonEmpty.startsWith('```')) {
                    // Remove preceding emoji line if present (📋, 📊, etc.)
                    if (fixedLines.length > 0 && /^[\p{Emoji}\s]*$/u.test(fixedLines[fixedLines.length - 1].trim()) && fixedLines[fixedLines.length - 1].trim().length <= 4) {
                      fixedLines.pop();
                    }
                    fixedLines.push('```mermaid');
                    inMermaid = true;
                  }
                }

                fixedLines.push(lines[i]);

                // Close mermaid block when we hit an empty line or a line that doesn't look like diagram content
                if (inMermaid && i < lines.length - 1) {
                  const nextTrimmed = lines[i + 1]?.trim() || '';
                  // Mermaid content lines are indented or start with section/title/keywords
                  const looksLikeMermaid = nextTrimmed === '' || /^[\s]/.test(lines[i + 1]) ||
                    /^(title|section|dateFormat|axisFormat|todayMarker|\w+\s*:|\"\w|"\w|x-axis|y-axis|bar |line )/.test(nextTrimmed);
                  // End if next line looks like regular markdown (starts with #, *, -, emoji, or uppercase word without colon)
                  const looksLikeMarkdown = /^[#*\-⚠🔴🎯📊📋⚡🔍❌✅>]/.test(nextTrimmed) || /^[A-Z][a-z]+ [A-Z]/.test(nextTrimmed);
                  if (looksLikeMarkdown && !looksLikeMermaid) {
                    fixedLines.push('```');
                    inMermaid = false;
                  }
                }
              }
              if (inMermaid) fixedLines.push('```');
              processedContent = fixedLines.join('\n');
            } catch (e) {
              // If preprocessing fails, use original content
              console.warn('Mermaid preprocessing failed:', e);
            }

            try {
              return (
                <ReactMarkdown
                  // SP4 J.1: remarkSpineCitation extracts
                  // [[spine:<persona_id>:<message_id>]] tokens BEFORE
                  // remarkGfm so GFM doesn't misinterpret the brackets.
                  remarkPlugins={[remarkSpineCitation, remarkGfm]}
                  rehypePlugins={[
                    // Explicitly tell rehype-highlight to ignore mermaid
                    [(rehypeHighlight as any), {
                      ignoreMissing: true,
                      ignore: ['mermaid'],
                      subset: false, // Don't throw on unknown languages
                      languages: {} // Empty languages object to prevent errors
                    }]
                  ]}
                  components={{
                    code({ node, inline, className, children, ...props }) {
                      try {
                        if (inline) {
                          return <code className={className} {...props}>{children}</code>;
                        }

                        // Use our custom CodeBlock component which handles mermaid specially
                        return <CodeBlock className={className} {...props}>{children}</CodeBlock>;
                      } catch (error) {
                        console.error("Error rendering code block:", error);
                        // Fallback to plain text if code rendering fails
                        return <pre className="p-2 bg-gray-800 rounded overflow-auto"><code>{String(children)}</code></pre>;
                      }
                    },
                    // Chat content shouldn't navigate the app away from the
                    // conversation. Default to new-tab for every link, which
                    // matches how [title](/artifacts/<id>) references are
                    // meant to land (see artifact-persistence directive in
                    // the system prompt). rel=noopener noreferrer closes the
                    // tab-nap escape hatch.
                    a({ node, href, children, ...props }) {
                      return (
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          {...props}
                        >
                          {children}
                        </a>
                      );
                    },
                    // SP4 J.1 — render spine-citation markers as
                    // <CitationPill>. The remarkSpineCitation plugin
                    // emits <span className="spine-citation-marker"
                    // data-spine-citation-{persona-id,spine-id}> via
                    // hProperties; this component intercepts that.
                    span({ node, className, children, ...props }) {
                      if (typeof className === 'string'
                          && className.includes('spine-citation-marker')) {
                        const personaId =
                          (props as any)['data-spine-citation-persona-id'];
                        const spineId =
                          (props as any)['data-spine-citation-spine-id'];
                        if (personaId && spineId) {
                          return (
                            <CitationPill
                              personaId={personaId}
                              spineId={spineId}
                            />
                          );
                        }
                      }
                      return <span className={className} {...props}>{children}</span>;
                    },
                  }}
                >
                  {processedContent}
                </ReactMarkdown>
              );
            } catch (error) {
              console.error("Error rendering markdown:", error);
              // Fallback to plain text if markdown rendering fails
              return <div className="whitespace-pre-wrap">{processedContent}</div>;
            }
          })()}
        </div>

        {/* GIS map artifacts render INSIDE the message body so they're
            visible by default — tool-call pills are telemetry, not
            deliverables. Filters tool_calls for any successful
            geojson_create and renders the inline map preview at full
            bubble width. The pill below stays as a collapsed metadata
            row (round, duration, status) for debugging. Dedup logic
            (last call wins per tool_name) lives in
            ../../utils/inlineMapToolFilter.ts. */}
        {message.role === 'assistant' &&
          dedupGisToolCalls(message.metadata?.tool_calls || []).map((tc: any, i: number) => {
            const result = tc.result;
            const inner = (result && typeof result === 'object' && 'result' in result)
              ? (result as any).result
              : result;
            // sessionId comes from the parent prop; fall back to the
            // message's own metadata for legacy messages that don't have
            // it threaded through.
            const sessId = sessionId
              ?? (message.metadata?.session_id as string | undefined)
              ?? (message as any)?.session_id;
            return (
              <div key={`map-${i}`} className="mt-2">
                <MapInlineRenderer
                  toolCall={{ tool_name: 'geojson_create', result: inner }}
                  darkMode={darkMode}
                  onSaveAsArtifact={async (fc, view) => {
                    const payload = {
                      session_id: sessId || '',
                      name: `Map ${new Date().toLocaleString()}`,
                      type: 'geojson' as any,
                      content: fc as any,
                      metadata: view ? { map_view: view } : undefined,
                    };
                    console.log('[Map Save] POST /api/artifacts/ payload', {
                      sessId,
                      hasFc: !!fc,
                      featureCount: fc?.features?.length ?? 0,
                      payload,
                    });
                    try {
                      const created = await createArtifact(payload);
                      const id = (created as any).id ?? '';
                      console.log('[Map Save] artifact created', { id, created });
                      return id;
                    } catch (err: any) {
                      // MapInlineRenderer's catch swallows the error and
                      // flips state back to ephemeral — that's the 'flash'
                      // Alan saw. Surface the full axios error so the
                      // root cause is visible in DevTools console.
                      console.error('[Map Save] failed', {
                        message: err?.message,
                        status: err?.response?.status,
                        data: err?.response?.data,
                        url: err?.config?.url,
                        method: err?.config?.method,
                        full: err,
                      });
                      throw err;
                    }
                  }}
                  onOpenInViewer={(artifactId, _fc, _view) => {
                    if (artifactId) {
                      navigate(`/artifacts/${artifactId}`);
                    } else {
                      // Not yet saved — open the in-place viewer flow
                      // would require a drawer infra we haven't built;
                      // for now nudge the user to save first.
                      console.warn('[Map Open] no artifact_id yet — click "Save as artifact" first');
                    }
                  }}
                />
              </div>
            );
          })}

        {/* 2026-05-03 P0 follow-up — parallel inline-render block for
            Mermaid producers (mindmap, generate_mermaid_diagram, future
            Mermaid-emitting tools). Pre-fix mindmap rendered as nothing
            visible (the tool returned a tree dict + HTML that no chat
            renderer parsed); post-fix mindmap_tool also emits
            `mermaid_code`, this block delegates to MermaidBlock for
            inline rendering. Same retry-collapse semantics as the GIS
            path — flash-lite's list[str] vs list[dict] retry round-trip
            now collapses to the converged result. */}
        {message.role === 'assistant' &&
          dedupMermaidToolCalls(message.metadata?.tool_calls || []).map((tc: any, i: number) => (
            <div key={`mermaid-${i}`} className="mt-2">
              <MermaidInlineRenderer toolCall={tc} darkMode={darkMode} />
            </div>
          ))}

        {/* Task 28 — tool-call pill grid is tucked behind the
            footer's `N tools` chevron chip. When open, pills render
            here (between markdown content and footer) grouped by LLM
            round. ToolCallPill handles per-call args/result
            expansion; consult_peers + create_artifacts delegate to
            specialized sub-renderers. Status palette lives in
            statusPalette.ts — shared by pill / footer / Wave 7 Panel. */}
        {message.role === 'assistant' &&
          toolCallsOpen &&
          message.metadata?.tool_calls?.length > 0 && (
            <div className="mt-2 mb-1 flex flex-col gap-2">
              {groupByRound(message.metadata.tool_calls).map((group) => {
                const showHeader = group.calls.length > 1 || group.round > 1;
                return (
                  <div key={`round-${group.round}`} className="flex flex-col gap-1">
                    {showHeader && (
                      <div className="text-[10px] font-mono opacity-50 flex items-center gap-2">
                        <span className="flex-1 h-px bg-gray-500/30" />
                        <span>
                          Round {group.round} — {group.calls.length}{' '}
                          {group.calls.length === 1 ? 'call' : 'calls'}
                        </span>
                        <span className="flex-1 h-px bg-gray-500/30" />
                      </div>
                    )}
                    <div className="flex flex-wrap gap-1.5">
                      {group.calls.map((tc, i) => (
                        <ToolCallPill
                          key={tc.identity?.tool_call_id ?? tc.tool_call_id ?? `${group.round}-${i}`}
                          call={tc}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

        {/* Task 28 — per-turn telemetry footer. Reads
            `message.metadata.turn_telemetry` (Wave 6 Task 24 shape).
            The `N tools` chip is a chevron toggle for the pill grid
            above. Degrades gracefully: missing cost → `~$—`, zero
            cached → omit the `(cached ↓)` segment, zero tools → drop
            the tool chip entirely. */}
        {message.role === 'assistant' && (message as any)?.metadata?.turn_telemetry && (
          <TurnTelemetryFooter
            telemetry={(message as any).metadata.turn_telemetry as TurnTelemetryShape}
            darkMode={darkMode}
            toolCountOverride={message.metadata?.tool_calls?.length ?? 0}
            toolCallsOpen={toolCallsOpen}
            onToggleToolCalls={
              message.metadata?.tool_calls?.length > 0
                ? () => setToolCallsOpen((v) => !v)
                : undefined
            }
          />
        )}

        {/* Legacy-message fallback — rehydrated messages from before
            the Wave 6 shape change have `tool_calls` but no
            `turn_telemetry`. Render a minimal toggle row so the pills
            are still reachable instead of silently gone. */}
        {message.role === 'assistant' &&
          !(message as any)?.metadata?.turn_telemetry &&
          message.metadata?.tool_calls?.length > 0 && (
            <div className="text-[11px] font-mono mt-1.5 pt-1.5 border-t border-gray-500/20 opacity-80">
              <button
                type="button"
                onClick={() => setToolCallsOpen((v) => !v)}
                aria-expanded={toolCallsOpen}
                className="inline-flex items-center gap-0.5 hover:opacity-100 underline-offset-2 hover:underline"
              >
                <span className="opacity-60">{toolCallsOpen ? '▾' : '▸'}</span>
                {message.metadata.tool_calls.length}{' '}
                {message.metadata.tool_calls.length === 1 ? 'tool' : 'tools'}
              </button>
            </div>
          )}

        {showTimestamp && (() => {
          // Assistant telemetry — drawn inline with the timestamp. Shape differs
          // between the immediate response (metadata.*) and historical blackboard
          // messages (top-level) — read both.
          const usage = (message as any)?.usage ?? (message as any)?.metadata?.usage;
          const durationMs = (message as any)?.duration_ms ?? (message as any)?.metadata?.duration_ms;
          const promptTokens = usage?.prompt_tokens;
          const completionTokens = usage?.completion_tokens;
          const totalTokens = usage?.total_tokens
            ?? ((promptTokens ?? 0) + (completionTokens ?? 0) || undefined);
          const tokensPerSec =
            completionTokens != null && durationMs != null && durationMs > 0
              ? Math.round(completionTokens / (durationMs / 1000))
              : null;
          const fmtDurationSec = (ms: number) => `${(ms / 1000).toFixed(1)}s`;
          const hasTelemetry =
            message.role === 'assistant' && (modelName || durationMs != null);
          const hasTooltip =
            promptTokens != null || completionTokens != null || durationMs != null || tokensPerSec != null;

          const timestampClass =
            message.role === 'user'
              ? 'text-blue-200'
              : message.role === 'error'
                ? darkMode ? 'text-red-300' : 'text-red-600'
                : darkMode ? 'text-gray-400' : 'text-gray-500';

          return (
            <div className={`text-xs mt-1 flex items-center gap-2 ${timestampClass}`}>
              <span>{formatMessageTime(message.timestamp)}</span>
              {hasTelemetry && (
                <>
                  {/* Separator sits OUTSIDE the hover group — hovering the dot
                      shouldn't pop the tooltip. Hover zone is tight to the
                      model tag + duration text only. */}
                  <span className="opacity-60">·</span>
                  <span
                    className={`relative group/telemetry font-mono inline-flex items-center gap-1.5 ${hasTooltip ? 'cursor-help' : ''}`}
                  >
                    {modelName && <span>{modelName}</span>}
                    {durationMs != null && (
                      <span className="opacity-80">{fmtDurationSec(durationMs)}</span>
                    )}
                    {hasTooltip && (
                      <div
                        className={`invisible group-hover/telemetry:visible absolute bottom-full left-0 mb-1 z-30 whitespace-nowrap rounded px-2 py-1.5 text-xs font-mono shadow-lg pointer-events-none ${
                          darkMode
                            ? 'bg-gray-900 border border-gray-600 text-gray-200'
                            : 'bg-gray-800 border border-gray-700 text-gray-100'
                        }`}
                      >
                        {modelName && (
                          <div className="font-semibold mb-0.5">{modelName}</div>
                        )}
                        {promptTokens != null && (
                          <div>
                            <span className="opacity-60">input  </span>
                            {promptTokens.toLocaleString()} tok
                          </div>
                        )}
                        {completionTokens != null && (
                          <div>
                            <span className="opacity-60">output </span>
                            {completionTokens.toLocaleString()} tok
                          </div>
                        )}
                        {totalTokens != null && totalTokens !== ((promptTokens ?? 0) + (completionTokens ?? 0) || null) && (
                          <div>
                            <span className="opacity-60">total  </span>
                            {totalTokens.toLocaleString()} tok
                          </div>
                        )}
                        {durationMs != null && (
                          <div>
                            <span className="opacity-60">latency </span>
                            {fmtDurationSec(durationMs)}
                          </div>
                        )}
                        {tokensPerSec != null && (
                          <div>
                            <span className="opacity-60">output </span>
                            ~{tokensPerSec.toLocaleString()} tok/s
                            <span className="opacity-50 ml-1">(approx)</span>
                          </div>
                        )}
                      </div>
                    )}
                  </span>
                </>
              )}
            </div>
          );
        })()}
      </div>
      )}
    </div>
    </>
  );
});
